import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { api } from "@/lib/kielo";
import { useApp } from "@/context/AppContext";
import { useI18n } from "@/i18n";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, XCircle, ListChecks, Trophy, RefreshCw, Zap, Flame, Layers, Shuffle, Star } from "lucide-react";
import { Button } from "@/components/ui/button";

const LEVEL_TONE = { A1: "#0E382B", A2: "#1E429F", B1: "#1A6B72" };

/* -------------------- Quiz hub -------------------- */
export function QuizHub() {
  const { progress } = useApp();
  const { t, lang } = useI18n();
  const [lessons, setLessons] = useState([]);
  const scores = progress?.quiz_scores || {};

  useEffect(() => {
    api.get("/curriculum", { params: { lang } }).then(({ data }) => setLessons(data.filter((l) => l.hasQuiz)));
  }, [lang]);

  const total = lessons.reduce((a, l) => a + (l.quizCount || 0), 0);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
      <h1 className="font-serif text-4xl sm:text-5xl font-semibold text-[#0E382B] tracking-tight">{t("quiz.hubTitle")}</h1>
      <p className="mt-2 text-[#52635B]">{t("quiz.hubSub")}</p>
      <p className="mt-1 text-sm font-semibold text-[#E46A28]" data-testid="quiz-hub-total">{total} {t("qx.questions")}</p>

      {["A1", "A2", "B1"].map((lv) => {
        const items = lessons.filter((l) => l.level === lv);
        if (!items.length) return null;
        return (
          <div key={lv} className="mt-10">
            <h2 className="flex items-center gap-2 text-lg font-semibold mb-4" style={{ color: LEVEL_TONE[lv] }}>
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: LEVEL_TONE[lv] }} /> {t(`journey.${lv}`)} · {lv}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((l) => <HubCard key={l.id} l={l} sc={scores[l.id]} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function HubCard({ l, sc }) {
  const { t } = useI18n();
  const pct = sc ? Math.round((sc.score / sc.total) * 100) : 0;
  return (
    <Link
      to={`/quizzes/${l.id}`}
      data-testid={`quiz-hub-item-${l.id}`}
      className="group bg-white rounded-2xl border border-[#E2DFD7] p-5 hover:-translate-y-1 hover:shadow-[0_14px_30px_rgba(14,56,43,0.10)] transition-[transform,box-shadow] duration-300"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-serif text-lg font-semibold text-[#16201B] leading-snug">{l.title}</h3>
          <p className="text-xs text-[#52635B] mt-1 flex items-center gap-1"><ListChecks className="w-3.5 h-3.5" /> {l.quizCount} {t("qx.questions")}</p>
        </div>
        <span className="shrink-0 w-10 h-10 rounded-full bg-[#E8F0EC] flex items-center justify-center text-[#0E382B] group-hover:bg-[#E46A28] group-hover:text-white transition-colors">
          {sc ? <Trophy className="w-5 h-5" /> : <Zap className="w-5 h-5" />}
        </span>
      </div>
      {sc && (
        <div className="mt-4">
          <div className="flex justify-between text-xs text-[#52635B] mb-1"><span>{t("qx.best")}</span><span className="font-semibold">{sc.score}/{sc.total}</span></div>
          <div className="h-1.5 rounded-full bg-[#F1EFEB] overflow-hidden"><div className="h-full bg-[#0E5A35]" style={{ width: `${pct}%` }} /></div>
        </div>
      )}
    </Link>
  );
}

/* -------------------- Mode picker -------------------- */
function ModePicker({ lessonId, total, onPick }) {
  const { t } = useI18n();
  const modes = [
    { key: "quick", icon: Shuffle, title: t("qx.quick"), desc: t("qx.quickDesc"), n: Math.min(10, total), accent: "#E46A28" },
    { key: "full", icon: Layers, title: t("qx.full"), desc: t("qx.fullDesc"), n: total, accent: "#0E382B" },
  ];
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 md:py-16">
      <Link to={`/lesson/${lessonId}`} className="inline-flex items-center gap-1.5 text-sm text-[#52635B] hover:text-[#0E382B] mb-6"><ArrowLeft className="w-4 h-4" /> {t("quiz.backLesson")}</Link>
      <h1 className="font-serif text-4xl font-semibold text-[#0E382B]">{t("qx.modeTitle")}</h1>
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
        {modes.map((m, i) => (
          <motion.button
            key={m.key}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.08 }}
            whileHover={{ y: -4 }}
            onClick={() => onPick(m.key)}
            data-testid={`quiz-mode-${m.key}`}
            className="text-left rounded-3xl bg-white border border-[#E2DFD7] p-7 hover:shadow-[0_18px_36px_rgba(14,56,43,0.12)] transition-shadow"
          >
            <span className="w-12 h-12 rounded-2xl flex items-center justify-center text-white" style={{ backgroundColor: m.accent }}><m.icon className="w-6 h-6" /></span>
            <h3 className="mt-5 font-serif text-2xl font-semibold text-[#16201B]">{m.title}</h3>
            <p className="mt-1 text-sm text-[#52635B]">{m.desc}</p>
            <p className="mt-5 text-sm font-semibold" style={{ color: m.accent }}>{m.n} {t("qx.questions")} →</p>
          </motion.button>
        ))}
      </div>
    </div>
  );
}

/* -------------------- Question body -------------------- */
function OptionList({ q, selected, setSelected, feedback, label }) {
  return (
    <div className={q.type === "tf" ? "grid grid-cols-2 gap-3" : "space-y-3"}>
      {q.options.map((opt, i) => {
        const chosen = selected === i;
        let cls = "border-[#E2DFD7] bg-white hover:border-[#0E382B]/40 hover:bg-[#F9F8F5]";
        if (feedback) {
          if (i === feedback.answer) cls = "border-[#7CC19B] bg-[#EAF5EF] text-[#0E5A35]";
          else if (chosen && !feedback.correct) cls = "border-[#F8B4B4] bg-[#FDF2F2] text-[#9B1C1C]";
          else cls = "border-[#E2DFD7] bg-white opacity-50";
        } else if (chosen) cls = "border-[#0E382B] bg-[#E8F0EC] shadow-[0_0_0_3px_rgba(14,56,43,0.08)]";
        return (
          <button key={i} disabled={!!feedback} data-testid={`quiz-option-${i}`} onClick={() => setSelected(i)}
            className={`w-full flex items-center gap-3 text-left px-4 py-3.5 rounded-xl border font-medium transition-[background-color,border-color,box-shadow,opacity] duration-200 ${cls}`}>
            <span className="w-7 h-7 shrink-0 rounded-lg bg-[#F1EFEB] text-[#52635B] text-xs font-bold flex items-center justify-center">{i + 1}</span>
            <span>{label(opt)}</span>
          </button>
        );
      })}
    </div>
  );
}

function FillInput({ value, setValue, feedback, onEnter }) {
  const { t } = useI18n();
  const state = feedback ? (feedback.correct ? "border-[#7CC19B] bg-[#EAF5EF] text-[#0E5A35]" : "border-[#F8B4B4] bg-[#FDF2F2] text-[#9B1C1C]") : "border-[#E2DFD7] focus:border-[#0E382B]";
  return (
    <input autoFocus data-testid="quiz-fill-input" value={value} disabled={!!feedback} onChange={(e) => setValue(e.target.value)}
      onKeyDown={(e) => e.key === "Enter" && onEnter()} placeholder={t("quiz.writeAnswer")}
      className={`w-full px-4 py-4 rounded-xl border-2 font-serif text-xl outline-none transition-colors ${state}`} />
  );
}

function HudBar({ idx, total, combo, xp, answered }) {
  const { t } = useI18n();
  return (
    <div className="flex items-center gap-4 mb-6">
      <div className="flex-1 h-2.5 rounded-full bg-[#E2DFD7] overflow-hidden">
        <div className="h-full bg-gradient-to-r from-[#0E382B] to-[#1A6B72] transition-[width] duration-500" style={{ width: `${((idx + (answered ? 1 : 0)) / total) * 100}%` }} />
      </div>
      <span data-testid="quiz-combo" className={`inline-flex items-center gap-1 text-sm font-bold px-2.5 py-1 rounded-full ${combo >= 2 ? "bg-[#FEF3C7] text-[#B45309]" : "bg-[#F1EFEB] text-[#52635B]"}`}>
        <Flame className="w-4 h-4" /> {combo}× <span className="hidden sm:inline font-medium">{t("qx.combo")}</span>
      </span>
      <span data-testid="quiz-xp" className="inline-flex items-center gap-1 text-sm font-bold px-2.5 py-1 rounded-full bg-[#E8F0EC] text-[#0E5A35]">
        <Star className="w-4 h-4" /> {xp} {t("qx.xp")}
      </span>
    </div>
  );
}

/* -------------------- Result -------------------- */
function ResultView({ graded, xp, bestCombo, mistakes, onRestart, onChangeMode, lessonId }) {
  const { t } = useI18n();
  const pct = Math.round((graded.score / graded.total) * 100);
  return (
    <div className="max-w-2xl mx-auto px-4 py-14">
      <motion.div initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="rounded-3xl bg-[#0E382B] text-white p-10 text-center ss-grain overflow-hidden" data-testid="quiz-result-score">
        <div className="w-20 h-20 mx-auto rounded-full bg-white/10 flex items-center justify-center animate-pop"><Trophy className="w-10 h-10 text-[#F6C9AD]" /></div>
        <h2 className="mt-5 font-serif text-5xl font-semibold">{graded.score} / {graded.total}</h2>
        <p className="mt-2 text-white/80">{pct === 100 ? t("quiz.resultPerfect") : pct >= 60 ? t("quiz.resultGood") : t("quiz.resultBad")}</p>
        <div className="mt-6 grid grid-cols-3 gap-3 text-center">
          {[[`${pct}%`, "%"], [xp, t("qx.earned")], [`${bestCombo}×`, t("qx.bestCombo")]].map(([v, l], i) => (
            <div key={i} className="rounded-2xl bg-white/10 py-3"><div className="text-2xl font-bold">{v}</div><div className="text-xs text-white/70">{l}</div></div>
          ))}
        </div>
        <div className="mt-7 flex flex-wrap justify-center gap-3">
          <Button onClick={onRestart} data-testid="quiz-restart-button" className="rounded-full bg-[#E46A28] hover:bg-[#c9591f] text-white"><RefreshCw className="w-4 h-4 mr-1.5" /> {t("quiz.again")}</Button>
          <Button onClick={onChangeMode} data-testid="quiz-result-change-mode" variant="outline" className="rounded-full bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white"><Layers className="w-4 h-4 mr-1.5" /> {t("qx.modeTitle")}</Button>
          <Link to={`/lesson/${lessonId}`}><Button variant="outline" className="rounded-full bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white">{t("quiz.backLesson")}</Button></Link>
        </div>
      </motion.div>
      <div className="mt-8" data-testid="quiz-mistake-review">
        <h3 className="font-serif text-2xl font-semibold text-[#0E382B] mb-4">{t("qx.review")}</h3>
        {mistakes.length === 0 ? <p className="text-[#0E5A35] font-medium">{t("qx.noMistakes")}</p> : mistakes.map((m, i) => (
          <div key={i} className="rounded-2xl bg-white border border-[#E2DFD7] p-5 mb-3">
            <p className="font-medium text-[#16201B]">{m.prompt}</p>
            <p className="mt-2 text-sm text-[#9B1C1C]"><XCircle className="w-4 h-4 inline mr-1" />{t("qx.yourAnswer")}: {m.given}</p>
            <p className="text-sm text-[#0E5A35]"><CheckCircle2 className="w-4 h-4 inline mr-1" />{m.correct}</p>
            <p className="mt-1 text-xs text-[#52635B]">{m.explanation}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* -------------------- Quiz runner -------------------- */
export function QuizRunner() {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const { user, saveQuizResult } = useApp();
  const { t, lang } = useI18n();
  const [meta, setMeta] = useState(null);
  const [mode, setMode] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState(null);
  const [fillValue, setFillValue] = useState("");
  const [answers, setAnswers] = useState({});
  const [feedback, setFeedback] = useState(null);
  const [combo, setCombo] = useState(0);
  const [bestCombo, setBestCombo] = useState(0);
  const [xp, setXp] = useState(0);
  const [mistakes, setMistakes] = useState([]);
  const [graded, setGraded] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/quizzes/${lessonId}`, { params: { lang, mode: "quick" } })
      .then(({ data }) => setMeta(data)).catch(() => navigate("/quizzes"));
  }, [lessonId, navigate, lang]);

  const start = async (m) => {
    const { data } = await api.get(`/quizzes/${lessonId}`, { params: { lang, mode: m } });
    setQuiz(data); setMode(m); setCurrent(0); setSelected(null); setFillValue(""); setAnswers({});
    setFeedback(null); setCombo(0); setBestCombo(0); setXp(0); setMistakes([]); setGraded(null);
  };

  const q = quiz?.questions[current];
  const isChoice = q && (q.type === "mc" || q.type === "tf");
  const label = (opt) => (q?.type === "tf" ? (opt === "True" ? t("qx.tTrue") : t("qx.tFalse")) : opt);

  const checkAnswer = useCallback(async () => {
    const given = isChoice ? selected : fillValue.trim();
    if (feedback || given === null || given === "") return;
    setAnswers((a) => ({ ...a, [q.id]: given }));
    const { data } = await api.post(`/quizzes/${lessonId}/grade`, { lesson_id: lessonId, answers: { [q.id]: given }, question_ids: [q.id] });
    const res = data.results[q.id];
    setFeedback(res);
    if (res.correct) {
      const c = combo + 1;
      setCombo(c); setBestCombo((b) => Math.max(b, c)); setXp((x) => x + 10 + Math.min(c - 1, 5) * 2);
    } else {
      setCombo(0);
      setMistakes((m) => [...m, { prompt: q.prompt, given: isChoice ? label(q.options[given]) : given, correct: isChoice ? label(q.options[res.answer]) : res.answer, explanation: res.explanation }]);
    }
  }, [isChoice, selected, fillValue, feedback, q, lessonId, combo]); // eslint-disable-line react-hooks/exhaustive-deps

  const next = useCallback(async () => {
    setFeedback(null); setSelected(null); setFillValue("");
    if (current + 1 < quiz.questions.length) return setCurrent((c) => c + 1);
    const { data } = await api.post(`/quizzes/${lessonId}/grade`, { lesson_id: lessonId, answers, question_ids: quiz.questions.map((x) => x.id) });
    setGraded(data);
    if (user) { try { await saveQuizResult(lessonId, data.score, data.total); } catch (e) { /* ignore */ } }
    if (data.score / data.total >= 0.8) confetti({ particleCount: 140, spread: 80, origin: { y: 0.6 }, colors: ["#0E382B", "#E46A28", "#F6C9AD", "#1E429F"] });
    toast.success(data.score === data.total ? t("quiz.resultPerfect") : `${data.score}/${data.total}`);
  }, [current, quiz, lessonId, answers, user, saveQuizResult, t]);

  useEffect(() => {
    const onKey = (e) => {
      if (!q || graded || e.target.tagName === "INPUT") return;
      const n = parseInt(e.key, 10);
      if (isChoice && !feedback && n >= 1 && n <= q.options.length) setSelected(n - 1);
      if (e.key === "Enter") { e.preventDefault(); feedback ? next() : checkAnswer(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [q, isChoice, feedback, graded, next, checkAnswer]);

  if (!meta) return <div className="max-w-2xl mx-auto px-4 py-20 text-[#52635B]">{t("lesson.loading")}</div>;
  if (!mode || !quiz) return <ModePicker lessonId={lessonId} total={meta.total_available} onPick={start} />;
  if (graded) return <ResultView graded={graded} xp={xp} bestCombo={bestCombo} mistakes={mistakes} onRestart={() => start(mode)} onChangeMode={() => { setGraded(null); setMode(null); }} lessonId={lessonId} />;

  const total = quiz.questions.length;
  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10 md:py-14">
      <button onClick={() => setMode(null)} data-testid="quiz-change-mode" className="inline-flex items-center gap-1.5 text-sm text-[#52635B] hover:text-[#0E382B] mb-6">
        <ArrowLeft className="w-4 h-4" /> {quiz.title}
      </button>
      <HudBar idx={current} total={total} combo={combo} xp={xp} answered={!!feedback} />
      <AnimatePresence mode="wait">
        <motion.div key={q.id} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.25 }}
          className="rounded-3xl bg-white border border-[#E2DFD7] p-7 shadow-[0_10px_30px_rgba(14,56,43,0.06)]">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium text-[#52635B]" data-testid="quiz-question-counter">{t("quiz.qOf")} {current + 1}/{total}</span>
            <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#FDEEE5] text-[#c9591f]" data-testid="quiz-question-type">{t(`qx.${q.type}`)}</span>
          </div>
          <h2 className="mt-3 font-serif text-2xl font-semibold text-[#16201B] leading-snug" data-testid="quiz-question-prompt">{q.prompt}</h2>
          <div className="mt-6">
            {isChoice ? <OptionList q={q} selected={selected} setSelected={setSelected} feedback={feedback} label={label} />
              : <FillInput value={fillValue} setValue={setFillValue} feedback={feedback} onEnter={() => (feedback ? next() : checkAnswer())} />}
          </div>
          {feedback && (
            <div data-testid="quiz-feedback" className={`mt-5 rounded-xl px-4 py-3.5 ${feedback.correct ? "bg-[#EAF5EF] animate-correct" : "bg-[#FDF2F2] animate-shake"}`}>
              <div className={`flex items-center gap-2 font-semibold ${feedback.correct ? "text-[#0E5A35]" : "text-[#9B1C1C]"}`}>
                {feedback.correct ? <CheckCircle2 className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
                {feedback.correct ? t("quiz.correct") : `${t("quiz.wrong")} ${isChoice ? label(q.options[feedback.answer]) : feedback.answer}`}
              </div>
              <p className="mt-1.5 text-sm text-[#52635B]">{feedback.explanation}</p>
            </div>
          )}
          <div className="mt-7">
            {!feedback ? (
              <Button onClick={checkAnswer} disabled={isChoice ? selected === null : !fillValue.trim()} data-testid="quiz-submit-button"
                className="w-full rounded-full h-12 bg-[#0E382B] hover:bg-[#1C5442] text-white text-base">{t("quiz.check")}</Button>
            ) : (
              <Button onClick={next} data-testid="quiz-next-question-button" className="w-full rounded-full h-12 bg-[#E46A28] hover:bg-[#c9591f] text-white text-base">
                {current + 1 < total ? t("quiz.next") : t("quiz.showResults")}
              </Button>
            )}
          </div>
        </motion.div>
      </AnimatePresence>
      <p className="mt-4 text-center text-xs text-[#B5B0A2] hidden md:block">{t("qx.keys")}</p>
    </div>
  );
}
